import React, { useEffect, useState, useCallback, useRef } from "react";
import { Bell, CalendarPlus, UserPlus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/http";
import { useAuth } from "@/context/AuthContext";

const ICONS = { online_booking: CalendarPlus, new_client: UserPlus };

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export const NotificationBell = ({ inline = false }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user && (user.role === "owner" || user.role === "admin");
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const seenRef = useRef(null); // ids we've already processed; null until first load

  const reviewBooking = useCallback((n) => {
    const params = new URLSearchParams();
    if (n.ref_date) params.set("date", n.ref_date);
    if (n.ref_id) params.set("open", n.ref_id);
    navigate(`/calendar${params.toString() ? `?${params.toString()}` : ""}`);
  }, [navigate]);

  const load = useCallback(() => {
    api.get("/notifications").then(({ data }) => {
      const list = data.items || [];
      setItems(list); setUnread(data.unread || 0);
      // Pop up a toast for brand-new online bookings (skip the backlog on first load).
      if (seenRef.current === null) {
        seenRef.current = new Set(list.map((n) => n.id));
      } else {
        list.filter((n) => n.type === "online_booking" && !n.read && !seenRef.current.has(n.id)).forEach((n) => {
          toast("New online booking", {
            description: n.body,
            duration: 12000,
            action: { label: "Review", onClick: () => reviewBooking(n) },
          });
        });
        list.forEach((n) => seenRef.current.add(n.id));
      }
    }).catch(() => {});
  }, [reviewBooking]);

  useEffect(() => {
    if (!isAdmin) return;
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [isAdmin, load]);

  if (!isAdmin) return null;

  const onOpenChange = (o) => {
    setOpen(o);
    if (o && unread > 0) api.post("/notifications/read-all").then(() => setUnread(0)).catch(() => {});
  };

  return (
    <div className={inline ? "flex-1 flex items-stretch" : "hidden xl:block fixed top-3 right-4 z-40"}>
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <button data-testid={inline ? "bnav-alerts" : "notif-bell"} aria-label="Notifications"
            className={inline
              ? "relative flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium text-muted-foreground active:scale-95"
              : "relative h-10 w-10 rounded-full bg-card border border-border shadow-sm flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"}>
            <Bell className={inline ? "h-[18px] w-[18px]" : "h-5 w-5"} />
            {inline && "Alerts"}
            {unread > 0 && (
              <span data-testid="notif-badge" className={`absolute ${inline ? "top-1 right-[calc(50%-18px)]" : "-top-0.5 -right-0.5"} min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center`}>
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-0" data-testid="notif-panel">
          <div className="px-4 py-3 border-b border-border font-heading font-semibold text-sm">Notifications</div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="notif-empty">No notifications yet.</div>
            ) : items.map((n) => {
              const Icon = ICONS[n.type] || Bell;
              const clickable = n.type === "online_booking" && (n.ref_id || n.ref_date);
              return (
                <div key={n.id} onClick={clickable ? () => { setOpen(false); reviewBooking(n); } : undefined}
                  className={`flex items-start gap-3 px-4 py-3 border-b border-border/60 ${n.read ? "" : "bg-primary/5"} ${clickable ? "cursor-pointer hover:bg-muted/60 transition-colors" : ""}`} data-testid={`notif-item-${n.id}`}>
                  <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-primary" /></div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{n.title}</div>
                    <div className="text-xs text-muted-foreground break-words">{n.body}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{timeAgo(n.created_at)}{clickable ? " · Tap to review" : ""}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
